// pages/app/notes/MobileDeleteNotePage.tsx
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useMutation } from '@tanstack/react-query'
import useApiService from 'services/api'
import { INote } from 'models/contact_companies'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { useQueryClient } from '@tanstack/react-query'

type LocationState = { note: INote }

const Page = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f3f4f6;
`

const Content = styled.div`
  flex: 1;
  padding: 24px 24px 32px;
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`

const Question = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #111827;
`

const Subtext = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 32px 0;
`

const ActionsRow = styled.div`
  width: 100%;
  display: flex;
  gap: 12px;
`

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 14px 16px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  border: ${(p) => (p.$variant === 'secondary' ? '1px solid #d1d5db' : 'none')};
  background: ${(p) => (p.$variant === 'secondary' ? '#ffffff' : '#065f46')};
  color: ${(p) => (p.$variant === 'secondary' ? '#111827' : '#ffffff')};
`

export function MobileDeleteNotePage() {
  const navigate = useNavigate()
  const { state } = useLocation() as { state: LocationState | null }
  const originalNote = state?.note
  const api = useApiService()
  const queryClient = useQueryClient()
  const [, setDeleted] = useState(false)

  const mutation = useMutation({
    mutationFn: () => {
      if (!originalNote) return Promise.reject(new Error('No note to delete'))
      return api.deleteNote(originalNote.id)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notes-infinite'] })
      await queryClient.refetchQueries({ queryKey: ['notes-infinite'] })
      toast.success('Note removed successfully')
      setDeleted(true)
      setTimeout(() => {
        navigate(-2) // back to notes list
      }, 800)
    },
  })

  if (!originalNote) {
    navigate(-1)
    return null
  }

  const handleCancel = () => {
    navigate(-2)
  }

  const handleDelete = () => {
    mutation.mutate()
  }

  return (
    <Page>
      <Content>
        <Question>Delete this note?</Question>
        <Subtext>This action cannot be undone.</Subtext>

        <ActionsRow>
          <Button $variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button $variant="primary" onClick={handleDelete}>
            Delete
          </Button>
        </ActionsRow>
      </Content>
    </Page>
  )
}
